import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./AfterMeGuideChat.css";

const GUIDE_COPY = {
  en: {
    launcher: "Guide",
    title: "AfterMe Guide",
    subtitle: "Planning help — not legal or medical advice.",
    greeting: "Hi {{firstName}}, here’s how to get started.",
    greetingFallback: "Here’s how to get started.",
    gettingStarted: "Getting started",
    nextSteps: "What to do next",
    safetyNote: "Safety note",
    trustNote:
      "AfterMe helps organize your wishes and memories. It does not provide legal, tax, medical, or estate advice.",
    intro:
      "Use this guide to set up the people, memories, and release rules that matter most to your plan.",
    startedItems: [
      "Add trusted family members",
      "Create your family chart",
      "Write private journals",
      "Record private messages",
      "Upload important photos/media",
      "Review release rules",
    ],
    nextItems: [
      "Complete your profile",
      "Add at least one trusted contact",
      "Create your first private journal",
      "Record one private message",
      "Review verification and release settings",
    ],
    ctas: {
      family: "Go to Family chart",
      journal: "Write a journal",
      media: "Open media gallery",
      verification: "Review verification",
    },
    badges: {
      priority: "Priority next step",
      completed: "Done",
      pending: "To do",
    },
    cards: {
      started: "Build the essentials first, then expand your plan over time.",
      next: "A few small steps now can make future planning much clearer.",
      safety: "Keep private details calm, intentional, and easy to revisit later.",
    },
    close: "Close guide",
  },
  fr: {
    launcher: "Guide",
    title: "Guide AfterMe",
    subtitle: "Aide a la planification — pas un avis juridique ou medical.",
    greeting: "Bonjour {{firstName}}, voici comment commencer.",
    greetingFallback: "Voici comment commencer.",
    gettingStarted: "Pour commencer",
    nextSteps: "Que faire ensuite",
    safetyNote: "Note de securite",
    trustNote:
      "AfterMe aide a organiser vos volontes et vos souvenirs. Ce n'est pas un avis juridique, fiscal, medical ou successoral.",
    intro:
      "Utilisez ce guide pour configurer les personnes, souvenirs et regles de diffusion qui comptent le plus pour votre plan.",
    startedItems: [
      "Ajouter des membres de famille de confiance",
      "Creer votre arbre familial",
      "Ecrire des journaux prives",
      "Enregistrer des messages prives",
      "Ajouter des photos et medias importants",
      "Verifier les regles de diffusion",
    ],
    nextItems: [
      "Completer votre profil",
      "Ajouter au moins un contact de confiance",
      "Creer votre premier journal prive",
      "Enregistrer un message prive",
      "Verifier les parametres de verification et de diffusion",
    ],
    ctas: {
      family: "Aller a l'arbre familial",
      journal: "Ecrire un journal",
      media: "Ouvrir la galerie media",
      verification: "Verifier les parametres",
    },
    badges: {
      priority: "Priorite",
      completed: "Termine",
      pending: "A faire",
    },
    cards: {
      started: "Commencez par l'essentiel, puis developpez votre plan a votre rythme.",
      next: "Quelques petites etapes maintenant peuvent rendre la suite beaucoup plus claire.",
      safety: "Gardez les details prives calmes, intentionnels et faciles a revoir plus tard.",
    },
    close: "Fermer le guide",
  },
};

function getFirstName(userName) {
  const name = String(userName || "").trim();
  if (!name) {
    return "";
  }
  return name.split(/\s+/)[0];
}

function GuideIcon({ type }) {
  if (type === "family") {
    return <span aria-hidden>👨‍👩‍👧</span>;
  }
  if (type === "journal") {
    return <span aria-hidden>✍️</span>;
  }
  if (type === "media") {
    return <span aria-hidden>🖼️</span>;
  }
  if (type === "verification") {
    return <span aria-hidden>🛡️</span>;
  }
  return <span aria-hidden>•</span>;
}

export function AfterMeGuideChat({
  authToken,
  locale = "en",
  userName = "",
  profileComplete = false,
  hasTrustedContact = false,
  onOpenFamilyChart,
  onWriteJournal,
  onOpenMediaGallery,
  onReviewVerification,
}) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const closeButtonRef = useRef(null);

  const copy = GUIDE_COPY[locale] || GUIDE_COPY.en;
  const firstName = getFirstName(userName);
  const greeting = firstName
    ? copy.greeting.replace("{{firstName}}", firstName)
    : copy.greetingFallback;

  const nextItems = useMemo(() => {
    const items = [
      { key: "profile", label: copy.nextItems[0], done: profileComplete },
      { key: "trusted", label: copy.nextItems[1], done: hasTrustedContact },
      { key: "journal", label: copy.nextItems[2], done: false },
      { key: "message", label: copy.nextItems[3], done: false },
      { key: "verification", label: copy.nextItems[4], done: false },
    ];
    return items.sort((a, b) => Number(a.done) - Number(b.done));
  }, [copy.nextItems, hasTrustedContact, profileComplete]);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== "Tab" || !panelRef.current) {
        return;
      }
      const focusable = panelRef.current.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) {
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [close, open]);

  if (!authToken) {
    return null;
  }

  const ctaButtons = [
    { key: "family", label: copy.ctas.family, onClick: onOpenFamilyChart },
    { key: "journal", label: copy.ctas.journal, onClick: onWriteJournal },
    { key: "media", label: copy.ctas.media, onClick: onOpenMediaGallery },
    { key: "verification", label: copy.ctas.verification, onClick: onReviewVerification },
  ];

  return (
    <>
      {!open ? (
        <button
          type="button"
          className="afterme-guide-chat-fab"
          onClick={() => setOpen(true)}
          aria-label={copy.title}
          aria-haspopup="dialog"
          aria-expanded={false}
        >
          <span className="afterme-guide-chat-fab-icon" aria-hidden>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12 3C7.03 3 3 6.58 3 11c0 2.13.9 4.08 2.4 5.5-.15 1.65-.98 3.1-2.35 4.05 2.05.05 4.05-.65 5.55-1.85.75.2 1.55.3 2.4.3 4.97 0 9-3.58 9-8s-4.03-8-9-8z"
                stroke="currentColor"
                strokeWidth="1.65"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className="afterme-guide-chat-fab-label">{copy.launcher}</span>
        </button>
      ) : null}

      {open ? (
        <div className="afterme-guide-chat-overlay" role="presentation">
          <button
            type="button"
            className="afterme-guide-chat-backdrop"
            aria-label={copy.close}
            onClick={close}
          />
          <div
            className="afterme-guide-chat"
            role="dialog"
            aria-modal="true"
            aria-label={copy.title}
            ref={panelRef}
          >
            <div className="afterme-guide-chat-panel">
              <header className="afterme-guide-chat-header">
                <div>
                  <h2 className="afterme-guide-chat-title">{copy.title}</h2>
                  <p className="afterme-guide-chat-sub">{copy.subtitle}</p>
                </div>
                <button
                  ref={closeButtonRef}
                  type="button"
                  className="afterme-guide-chat-close"
                  onClick={close}
                  aria-label={copy.close}
                >
                  ×
                </button>
              </header>

              <div className="afterme-guide-chat-body">
                <section className="afterme-guide-chat-intro-card">
                  <p className="afterme-guide-chat-intro-eyebrow">{copy.launcher}</p>
                  <h3 className="afterme-guide-chat-intro-title">{greeting}</h3>
                  <p className="afterme-guide-chat-intro-copy">{copy.intro}</p>
                </section>

                <div className="afterme-guide-chat-sections">
                  <section className="afterme-guide-chat-section-card">
                    <div className="afterme-guide-chat-section-head">
                      <h3>{copy.gettingStarted}</h3>
                      <p>{copy.cards.started}</p>
                    </div>
                    <ul className="afterme-guide-chat-bullets">
                      {copy.startedItems.map((item) => (
                        <li key={item}>
                          <span className="afterme-guide-chat-bullet-icon" aria-hidden>
                            ✓
                          </span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </section>

                  <section className="afterme-guide-chat-section-card">
                    <div className="afterme-guide-chat-section-head">
                      <h3>{copy.nextSteps}</h3>
                      <p>{copy.cards.next}</p>
                    </div>
                    <ul className="afterme-guide-chat-checklist">
                      {nextItems.map((item, index) => (
                        <li key={item.key} className={!item.done && index === 0 ? "priority" : ""}>
                          <span className={`afterme-guide-chat-check ${item.done ? "done" : "pending"}`}>
                            {item.done ? copy.badges.completed : !item.done && index === 0 ? copy.badges.priority : copy.badges.pending}
                          </span>
                          <span>{item.label}</span>
                        </li>
                      ))}
                    </ul>
                  </section>

                  <section className="afterme-guide-chat-section-card afterme-guide-chat-safety-card">
                    <div className="afterme-guide-chat-section-head">
                      <h3>{copy.safetyNote}</h3>
                      <p>{copy.cards.safety}</p>
                    </div>
                    <p className="afterme-guide-chat-safety-copy">{copy.trustNote}</p>
                  </section>
                </div>

                <div className="afterme-guide-chat-cta-grid">
                  {ctaButtons.map((button) => (
                    <button
                      key={button.key}
                      type="button"
                      className="afterme-guide-chat-cta"
                      onClick={() => {
                        close();
                        button.onClick?.();
                      }}
                    >
                      <span className="afterme-guide-chat-cta-icon">
                        <GuideIcon type={button.key} />
                      </span>
                      <span>{button.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
